/**
 * Errori che il server restituisce: li legge l'utente nell'interfaccia e chi
 * chiama le API dalla CI. Gli identificativi e i dettagli di Docker o del proxy
 * entrano come parametri, così come arrivano.
 */
export const errori = {
  // Generici
  "errori.interno": "Errore interno del server",
  "errori.testo": (p: { testo: string }) => p.testo,
  "errori.autenticazioneRichiesta": "Autenticazione richiesta",
  "errori.permessiInsufficienti": "Permessi insufficienti",
  "errori.troppiTentativi": (p: { secondi: number }) => `troppi tentativi: riprova fra ${p.secondi} secondi`,
  "errori.origineDiversa": "richiesta da un'origine diversa da Portman",
  "errori.permessoRichiesto": (p: { permesso: string }) =>
    `operazione non consentita: richiede il permesso ${p.permesso}`,
  "errori.endpointNonTrovato": "endpoint non trovato",
  "errori.datiNonValidi": (p: { problemi: string }) => `dati non validi: ${p.problemi}`,
  "errori.corpoNonJson": "il corpo della richiesta non è JSON valido",
  "errori.idNonValido": (p: { valore: string }) => `identificativo non valido: ${p.valore}`,

  // Risorse non trovate
  "errori.stackNonTrovato": (p: { id: number | string }) => `stack ${p.id} non trovato`,
  "errori.revisioneNonTrovata": (p: { id: number | string }) => `revisione ${p.id} non trovato`,
  "errori.deployNonTrovato": (p: { id: number | string }) => `deploy ${p.id} non trovato`,
  "errori.utenteNonTrovato": (p: { id: number | string }) => `utente ${p.id} non trovato`,
  "errori.tokenNonTrovato": (p: { id: number | string }) => `token ${p.id} non trovato`,
  "errori.containerNonTrovato": (p: { id: string }) => `container ${p.id} non trovato`,
  "errori.cartellaNonTrovata": (p: { percorso: string }) => `cartella ${p.percorso} non trovato`,
  "errori.progettoNonTrovato": (p: { progetto: string }) => `progetto Compose ${p.progetto} non trovato`,
  "errori.risorsaDockerNonTrovata": (p: { dettaglio: string }) => `risorsa Docker: ${p.dettaglio} non trovato`,

  // Docker e Compose
  "errori.composeAnnullato": (p: { comando: string }) =>
    `"docker compose ${p.comando}" è stata annullata: quanto già fatto resta come è, Compose non torna indietro da solo.`,
  "errori.composeFallito": (p: { comando: string; codice: number | null; dettaglio: string }) =>
    `"docker compose ${p.comando}" è terminato con codice ${p.codice}: ${p.dettaglio}`,
  "errori.dockerIrraggiungibile": (p: { socket: string }) =>
    `Docker non è raggiungibile su ${p.socket}. Verifica che il daemon sia attivo e che Portman abbia accesso al socket.`,
  "errori.dockerRifiuta": (p: { dettaglio: string }) => `Docker ha rifiutato la richiesta: ${p.dettaglio}`,
  "errori.logIlleggibili": (p: { dettaglio: string }) => `impossibile leggere i log: ${p.dettaglio}`,
  "errori.eventiIlleggibili": (p: { dettaglio: string }) => `impossibile leggere gli eventi: ${p.dettaglio}`,
  "errori.reteCollega": (p: { container: string; rete: string; dettaglio: string }) =>
    `impossibile collegare il container ${p.container} alla rete ${p.rete}: ${p.dettaglio}`,
  "errori.reteScollega": (p: { container: string; rete: string; dettaglio: string }) =>
    `impossibile scollegare il container ${p.container} dalla rete ${p.rete}: ${p.dettaglio}`,
  "errori.volumeRimozione": (p: { nome: string; dettaglio: string }) =>
    `impossibile rimuovere il volume ${p.nome}: ${p.dettaglio}`,
  "errori.immagineCopia": (p: { immagine: string }) =>
    `impossibile scaricare ${p.immagine} per copiare i volumi`,
  "errori.copiaVolume": (p: { da: string; a: string; codice: number }) =>
    `la copia di ${p.da} in ${p.a} è terminata con codice ${p.codice}`,

  // Nginx Proxy Manager
  "errori.npmIrraggiungibile": (p: { indirizzo: string }) => `Nginx Proxy Manager non è raggiungibile su ${p.indirizzo}.`,
  "errori.npmCredenziali":
    "Nginx Proxy Manager ha rifiutato le credenziali configurate. Verifica indirizzo email e password in PORTMAN_NPM_IDENTITY e PORTMAN_NPM_SECRET.",
  "errori.npmToken": "Nginx Proxy Manager non ha restituito un token valido.",
  "errori.npmRifiuta": (p: { codice: number }) => `Nginx Proxy Manager ha rifiutato la richiesta (codice ${p.codice}).`,
  "errori.proxyStackAssente": (p: { stack: string }) => `lo stack "${p.stack}" non esiste più.`,
  "errori.proxyStackEsiste": (p: { stack: string }) => `lo stack "${p.stack}" esiste già.`,
  "errori.proxyGiaConfigurato":
    "il proxy configurato con le variabili PORTMAN_NPM_* risponde già, oppure punta a un indirizzo diverso da quello dell'installazione.",

  // Stack
  "errori.stackNomeDuplicato": (p: { nome: string }) => `esiste già uno stack chiamato "${p.nome}"`,
  "errori.stackNomeNonValido": (p: { nome: string }) =>
    `"${p.nome}" non è un nome valido: usa solo lettere minuscole, cifre, trattini e underscore, iniziando con una lettera o una cifra. È il vincolo che Docker Compose impone al nome del progetto.`,
  "errori.composeIlleggibile": (p: { dettaglio: string }) => `il file compose non è leggibile: ${p.dettaglio}`,
  "errori.stackNomeAmbiguo": (p: { nome: string; rivale: string }) =>
    `"${p.nome}" si confonderebbe con lo stack "${p.rivale}": i rilasci blue/green usano i progetti nome-a e nome-b. Scegli un altro nome.`,
  "errori.progettoEstraneo": (p: { progetto: string }) =>
    `sull'host esiste già il progetto Compose "${p.progetto}", che Portman non gestisce: per portarlo in Portman usa l'importazione, altrimenti scegli un altro nome.`,
  "errori.revisioneAssente": (p: { stack: string; numero: number }) =>
    `lo stack "${p.stack}" non ha una revisione numero ${p.numero}`,
  "errori.stackStessoNome": (p: { nome: string }) => `lo stack si chiama già "${p.nome}".`,
  "errori.proxyNonRinominabile": (p: { stack: string }) =>
    `lo stack "${p.stack}" non si può rinominare: Portman lo cerca per nome.`,
  "errori.stackNonAvviabile": (p: { dettaglio: string }) => `lo stack non può essere avviato: ${p.dettaglio}`,
  "errori.conflittoPorte": (p: { dettaglio: string }) => `conflitto di porte: ${p.dettaglio}`,
  "errori.conflittoNomi": (p: { dettaglio: string }) =>
    `conflitto di nomi: ${p.dettaglio}. I nomi fissati con container_name sono unici su tutto il demone Docker: rimuovi i container che li occupano, oppure cambia i nomi in questo stack.`,
  "errori.eliminaInCorso": (p: { stack: string }) =>
    `un'operazione è in corso su "${p.stack}": attendi che finisca prima di eliminarlo.`,
  "errori.eliminaInEsecuzione": (p: { stack: string }) => `"${p.stack}" è in esecuzione: fermalo prima di eliminarlo.`,
  "errori.porteDaTogliere": "non c'è alcuna porta host che io possa togliere da sola da questo compose.",
  "errori.servizioFissoInstradato": (p: { servizio: string }) =>
    `il servizio "${p.servizio}" è fisso: non ha generazioni su cui spostare il traffico.`,
  "errori.portaAssegnata": (p: { porta: number; protocollo: string }) =>
    `la porta ${p.porta}/${p.protocollo} è già assegnata a un altro stack`,
  "errori.volumeEsiste": (p: { volume: string }) =>
    `il volume "${p.volume}" esiste già: eliminalo oppure scegli un altro nome.`,
  "errori.importNomeDuplicato": (p: { nome: string }) =>
    `esiste già uno stack chiamato "${p.nome}": se vuoi reimportarlo, eliminalo prima oppure scegli un altro nome.`,

  // Rilascio
  "errori.reteNonPronta": (p: { dettaglio: string; stato: string }) =>
    `${p.dettaglio}. Lo stato del container è "${p.stato}": se non è in esecuzione stabile, è quello da guardare prima della rete.`,
  "errori.nonPronto": (p: { container: string; secondi: number; motivo: string }) =>
    `${p.container} non è diventato pronto dopo ${p.secondi} s: ${p.motivo}. Il traffico non è stato spostato: la versione precedente è ancora quella attiva.`,
  "errori.serveProxy":
    "il rilascio senza interruzione richiede un proxy. Installalo dalla scheda Rilascio, oppure imposta PORTMAN_NPM_URL, PORTMAN_NPM_IDENTITY e PORTMAN_NPM_SECRET.",
  "errori.senzaDominio": (p: { stack: string }) =>
    `lo stack "${p.stack}" non ha alcun dominio configurato: senza un instradamento non è possibile spostare il traffico da una versione all'altra.`,

  // Utenti e accesso
  "errori.passwordDebole": (p: { problemi: string }) => `la password non è abbastanza robusta: ${p.problemi}`,
  "errori.giaConfigurato": "Portman è già configurato: il primo amministratore esiste",
  "errori.tokenSetup": "Token di primo avvio non valido: lo trovi nel log di avvio di Portman",
  "errori.credenziali": "Credenziali non valide",
  "errori.passwordAttuale": "la password attuale non è corretta",
  "errori.usernameDuplicato": (p: { username: string }) => `esiste già un utente con username "${p.username}"`,
  "errori.usernameVuoto": "l'username non può essere vuoto",
  "errori.ultimoAdmin":
    "operazione rifiutata: questo è l'ultimo amministratore attivo e senza di esso nessuno potrebbe più amministrare Portman. Nominane prima un altro.",
  "errori.eliminaSeStessi": "non è possibile eliminare se stessi",

  // Backup
  "errori.backupNonSqlite": "il file caricato non è un database SQLite leggibile",
  "errori.backupDanneggiato": "il database caricato è danneggiato e non può essere ripristinato",
  "errori.backupNonPortman": "il database caricato non è un backup di Portman: manca la tabella delle migrazioni",
  "errori.backupSenzaUtenti":
    "il backup non contiene alcun utente: ripristinarlo chiuderebbe tutti fuori da Portman",
  "errori.backupVuoto": "il file caricato è vuoto",

  // Cartelle
  "errori.cartellaNonVuota": (p: { percorso: string }) => `la cartella ${p.percorso} non è vuota`,
  "errori.cartellaPermesso": (p: { percorso: string }) =>
    `permesso negato su ${p.percorso}: l'uid 1000 di Portman deve poter scrivere lì`,
  "errori.cartelleSpente":
    "gestione cartelle non attiva: imposta PORTMAN_STORAGE_ROOT e monta quella cartella nel container",
  "errori.percorsoFuoriRadice": (p: { percorso: string; radice: string }) =>
    `il percorso "${p.percorso}" esce dalla radice ${p.radice}`,
  "errori.cartellaNome": "indicare il nome della cartella",
  "errori.cartellaEsiste": (p: { percorso: string }) => `la cartella ${p.percorso} esiste già`,
  "errori.radiceNonEliminabile": "la radice non si elimina",

  // Email
  "errori.linguaDaAmbiente": "la lingua è impostata da PORTMAN_LANG: per cambiarla modifica la variabile e riavvia Portman",
  "errori.provaEmailIncompleta": "per inviare una prova servono il server SMTP e almeno un destinatario",
  "errori.invioEmail": (p: { dettaglio: string }) => `invio non riuscito: ${p.dettaglio}`,

  // Token di lettura e registry
  "errori.tokenNome": (p: { max: number }) => `il nome deve avere da 1 a ${p.max} caratteri`,
  "errori.tokenDurata": (p: { ammesse: string }) => `durata non valida: ${p.ammesse}`,
  "errori.tokenStack": "indicare almeno uno stack",
  "errori.stackInesistente": (p: { id: number }) => `nessuno stack con identificativo ${p.id}`,
  "errori.registryIndirizzo": "indicare l'indirizzo del registry",
  "errori.registryUtente": "indicare l'utente del registry",
  "errori.registryPassword": "indicare la password del registry",
  "errori.registryInesistente": (p: { id: number }) => `nessun registry con identificativo ${p.id}`,

  // Validazione dei campi (le altre frasi di Zod arrivano dalla sua traduzione)
  "validazione.corpo": "corpo non valido",
  "validazione.porteProxyUguali": "le porte HTTP e HTTPS devono essere diverse",
  "validazione.finestraInvertita": "da deve precedere a",
  "validazione.finestraTroppoLunga": "la finestra non può superare 7 giorni",
  "validazione.nessunCampo": "indicare almeno un campo da modificare",
  "validazione.nomeVariabile": "nome di variabile non valido",
  "validazione.nomeVariabileDettaglio": "nome di variabile non valido (lettere, cifre e underscore)",
  "validazione.descrizioneLunga": "la descrizione supera i 500 caratteri",

  // Webhook
  "errori.webhookToken": "token del webhook non valido",
  "errori.webhookSenzaTag": 'la chiamata deve indicare il tag da rilasciare, ad esempio {"tag":"v1.2.3"}',
  "errori.webhookTagNonValido": (p: { tag: string }) =>
    `"${p.tag}" non è un tag valido: sono ammessi lettere, cifre, punti, trattini e underscore.`,
  "errori.tagNonUsato": (p: { variabile: string }) =>
    `nessuna immagine del compose usa \${${p.variabile}}: scrivi ad esempio image: app:\${${p.variabile}}`,
  "errori.ipBloccato": "accesso negato: questo indirizzo è bloccato",
  "errori.ipTuo": "non puoi bloccare l'indirizzo da cui sei collegato",
  "validazione.ip": "indirizzo IP non valido",
};
